import React, { useEffect, useState } from 'react';
import { Table, Tag, Typography, Select, Button, Alert, Card, Modal, Form, Input, message, Drawer, Descriptions } from 'antd';
import { PlusOutlined, CheckCircleOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { getProjects, getSiteInstructions, getUsers, getRoles, createSiteInstruction } from '../services/api';

const { Title, Text } = Typography;
const { TextArea } = Input;

interface Project {
  id: string;
  code: string;
  name: string;
}

interface UserOption {
  id: string;
  full_name: string;
}

interface RoleOption {
  id: string;
  name: string;
}

const SiteInstructionsPage: React.FC = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProject, setSelectedProject] = useState<string>('');
  const [instructions, setInstructions] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [modalOpen, setModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();
  const [users, setUsers] = useState<UserOption[]>([]);
  const [roles, setRoles] = useState<RoleOption[]>([]);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedInstruction, setSelectedInstruction] = useState<any>(null);

  useEffect(() => {
    loadProjects();
    loadFormData();
  }, []);

  const loadProjects = async () => {
    try {
      const response = await getProjects();
      setProjects(response.data);
      if (response.data.length > 0) {
        setSelectedProject(response.data[0].id);
        loadInstructions(response.data[0].id);
      }
    } catch (err) {
      setError('Failed to load projects.');
    }
  };

  const loadFormData = async () => {
    try {
      const [usersRes, rolesRes] = await Promise.all([getUsers(), getRoles()]);
      setUsers(usersRes.data);
      setRoles(rolesRes.data);
    } catch (err) {
      // Non-blocking — names simply won't resolve if this fails
    }
  };

  const loadInstructions = async (projectId: string) => {
    setLoading(true);
    try {
      const response = await getSiteInstructions(projectId);
      setInstructions(response.data);
    } catch (err) {
      setError('Failed to load site instructions.');
    } finally {
      setLoading(false);
    }
  };

  const handleProjectChange = (value: string) => {
    setSelectedProject(value);
    loadInstructions(value);
  };

  const userName = (id: string) => users.find(u => u.id === id)?.full_name || id;
  const roleName = (id: string) => roles.find(r => r.id === id)?.name || id;

  const openDrawer = (record: any) => {
    setSelectedInstruction(record);
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setSelectedInstruction(null);
  };

  const openModal = async () => {
    form.resetFields();
    setModalOpen(true);
    if (users.length === 0 || roles.length === 0) {
      loadFormData();
    }
  };

  const closeModal = () => {
    setModalOpen(false);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      const payload = {
        instruction_text: values.instruction_text,
        recipient: values.recipient,
        recipient_role: values.recipient_role,
      };
      await createSiteInstruction(selectedProject, payload);
      message.success('Site instruction issued successfully.');
      setModalOpen(false);
      loadInstructions(selectedProject);
    } catch (err: any) {
      if (err?.errorFields) {
        return;
      }
      const apiMessage = err?.response?.data?.detail
        || err?.response?.data?.instruction_text?.[0]
        || 'Failed to issue instruction.';
      message.error(apiMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const columns = [
    {
      title: 'Instruction Text',
      dataIndex: 'instruction_text',
      key: 'instruction_text',
      width: 350,
      ellipsis: true,
      render: (text: string) => (
        <Text style={{ color: '#1B2A4A' }}>{text}</Text>
      ),
    },
    {
      title: 'Recipient',
      dataIndex: 'recipient',
      key: 'recipient',
      render: (id: string) => id ? userName(id) : '—',
    },
    {
      title: 'Acknowledgment',
      dataIndex: 'acknowledged_at',
      key: 'acknowledged_at',
      render: (date: string) => date
        ? (
          <Tag icon={<CheckCircleOutlined />} color="green">
            Acknowledged {new Date(date).toLocaleDateString('en-IN')}
          </Tag>
        )
        : (
          <Tag icon={<ClockCircleOutlined />} color="orange">
            Pending
          </Tag>
        ),
    },
    {
      title: 'Issued On',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => new Date(date).toLocaleDateString('en-IN', {
        day: '2-digit', month: 'short', year: 'numeric'
      }),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <Title level={4} style={{ margin: 0, color: '#1B2A4A' }}>
          Site Instructions
        </Title>
        <div style={{ display: 'flex', gap: 12 }}>
          <Select
            value={selectedProject}
            onChange={handleProjectChange}
            style={{ width: 250 }}
            options={projects.map(p => ({ value: p.id, label: `${p.code} — ${p.name}` }))}
          />
          <Button type="primary" icon={<PlusOutlined />} style={{ background: '#1B2A4A' }} onClick={openModal} disabled={!selectedProject}>
            Issue Instruction
          </Button>
        </div>
      </div>

      {error && <Alert message={error} type="error" showIcon style={{ marginBottom: 24 }} />}

      <Card style={{ borderRadius: 12 }}>
        <Table
          columns={columns}
          dataSource={instructions}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10 }}
          locale={{ emptyText: 'No site instructions found for this project.' }}
          onRow={(record) => ({
            onClick: () => openDrawer(record),
            style: { cursor: 'pointer' },
          })}
        />
      </Card>

      <Drawer
        title="Site Instruction Details"
        open={drawerOpen}
        onClose={closeDrawer}
        width={480}
      >
        {selectedInstruction && (
          <>
            <div style={{ marginBottom: 16 }}>
              {selectedInstruction.acknowledged_at ? (
                <Tag icon={<CheckCircleOutlined />} color="green">
                  Acknowledged {new Date(selectedInstruction.acknowledged_at).toLocaleDateString('en-IN')}
                </Tag>
              ) : (
                <Tag icon={<ClockCircleOutlined />} color="orange">Pending</Tag>
              )}
            </div>
            <Descriptions column={1} bordered size="small">
              <Descriptions.Item label="Instruction Text">
                <span style={{ whiteSpace: 'pre-wrap' }}>{selectedInstruction.instruction_text || '—'}</span>
              </Descriptions.Item>
              <Descriptions.Item label="Recipient">
                {selectedInstruction.recipient ? userName(selectedInstruction.recipient) : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Recipient Role">
                {selectedInstruction.recipient_role ? roleName(selectedInstruction.recipient_role) : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Acknowledgment Remarks">
                {selectedInstruction.acknowledgment_remarks || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Issued On">
                {selectedInstruction.created_at ? new Date(selectedInstruction.created_at).toLocaleDateString('en-IN') : '—'}
              </Descriptions.Item>
            </Descriptions>
          </>
        )}
      </Drawer>

      <Modal
        title="Issue Site Instruction"
        open={modalOpen}
        onCancel={closeModal}
        onOk={handleSubmit}
        confirmLoading={submitting}
        okText="Issue Instruction"
        width={560}
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="instruction_text"
            label="Instruction Text"
            rules={[
              { required: true, message: 'Instruction text is required' },
              { min: 10, message: 'Instruction must be at least 10 characters' },
            ]}
          >
            <TextArea rows={4} placeholder="Describe the instruction being issued" />
          </Form.Item>

          <Form.Item
            name="recipient"
            label="Recipient"
            rules={[{ required: true, message: 'Recipient is required' }]}
          >
            <Select
              placeholder="Select recipient"
              options={users.map(u => ({ value: u.id, label: u.full_name }))}
            />
          </Form.Item>

          <Form.Item
            name="recipient_role"
            label="Recipient Role"
            rules={[{ required: true, message: 'Recipient role is required' }]}
          >
            <Select
              placeholder="Select the recipient's role"
              options={roles.map(r => ({ value: r.id, label: r.name }))}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default SiteInstructionsPage;